import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnPrimary, btnSecondary, input, label } from '../styles/theme.js'
import { projects as projectsApi } from '../lib/api.js'

const header = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${space.xl};
`

const title = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
`

const grid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${space.lg};
`

const projectCard = css`
  ${card};
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  &:hover {
    border-color: ${colors.brand};
    box-shadow: 0 2px 8px ${colors.shadow};
  }
`

const projectName = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.xs};
`

const projectDesc = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  line-height: 1.5;
`

const projectMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: ${space.sm};
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textSecondary};
`

const modal = css`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  z-index: 100;
`

const modalCard = css`
  ${card};
  width: 100%;
  max-width: 500px;
`

const fieldGroup = css`
  margin-bottom: ${space.md};
`

const modalActions = css`
  display: flex;
  justify-content: flex-end;
  gap: ${space.sm};
  margin-top: ${space.lg};
`

export function ProjectsView() {
  let projectList = []
  let loading = true
  let showCreate = false
  let newName = ''
  let newDesc = ''
  let creating = false

  async function load() {
    loading = true
    try {
      projectList = await projectsApi.list()
    } catch (err) {
      console.error('Failed to load projects:', err)
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function create(e) {
    e.preventDefault()
    creating = true
    try {
      const project = await projectsApi.create({ name: newName, description: newDesc })
      showCreate = false
      newName = ''
      newDesc = ''
      m.route.set(`/projects/${project.id}`)
    } catch (err) {
      console.error('Failed to create project:', err)
    } finally {
      creating = false
      m.redraw()
    }
  }

  return {
    oninit: load,
    view() {
      return m('div', [
        m('div', { class: header }, [
          m('h1', { class: title }, 'Projects'),
          m('button', {
            class: btnPrimary,
            onclick: () => { showCreate = true },
          }, '+ New project'),
        ]),

        loading
          ? m('div', { class: emptyState }, 'Loading...')
          : projectList.length === 0
            ? m('div', { class: emptyState }, [
                m('p', { class: css`font-size: ${font.size.lg}; margin-bottom: ${space.sm};` },
                  'No projects yet'),
                m('p', 'Create your first project to get started.'),
              ])
            : m('div', { class: grid },
                projectList.map((p) =>
                  m('div', {
                    class: projectCard,
                    onclick: () => m.route.set(`/projects/${p.id}`),
                  }, [
                    m('div', { class: projectName }, p.name),
                    p.description
                      ? m('div', { class: projectDesc }, p.description)
                      : null,
                    m('div', { class: projectMeta },
                      `Created ${new Date(p.created_at).toLocaleDateString()}`),
                  ])
                )
              ),

        showCreate
          ? m('div', { class: modal, onclick: (e) => { if (e.target === e.currentTarget) showCreate = false } }, [
              m('form', { class: modalCard, onsubmit: create }, [
                m('h2', { class: css`font-size: ${font.size.xl}; font-weight: ${font.weight.semibold}; margin-bottom: ${space.lg};` },
                  'New project'),

                m('div', { class: fieldGroup }, [
                  m('label', { class: label }, 'Project name'),
                  m('input', {
                    class: input,
                    value: newName,
                    oninput: (e) => { newName = e.target.value },
                    placeholder: 'e.g., Housing Bot Audit Q1 2026',
                    required: true,
                    autofocus: true,
                  }),
                ]),

                m('div', { class: fieldGroup }, [
                  m('label', { class: label }, 'Description'),
                  m('textarea', {
                    class: `${input} ${css`min-height: 100px; resize: vertical;`}`,
                    value: newDesc,
                    oninput: (e) => { newDesc = e.target.value },
                    placeholder: 'What are you auditing and why?',
                  }),
                ]),

                m('div', { class: modalActions }, [
                  m('button', {
                    class: btnSecondary,
                    type: 'button',
                    onclick: () => { showCreate = false },
                  }, 'Cancel'),
                  m('button', {
                    class: btnPrimary,
                    type: 'submit',
                    disabled: creating || !newName.trim(),
                  }, creating ? 'Creating...' : 'Create project'),
                ]),
              ]),
            ])
          : null,
      ])
    },
  }
}
